<script setup lang="ts">
import type { StoredUrl } from "@/services/api-inventory/apiInventory.types";
import { useApiStore } from "@/stores/api/useApiStore";
import { constructUrl } from "@/utils/utils";
import {
  HaskoningCombobox,
  type HaskoningComboboxOption,
  type HaskoningComboboxSelectedChipStatus,
} from "@haskoning/vue-component-library";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

const apiStore = useApiStore();
const { baseUrl, rawUrls, inventory, apiEntries, isInventoryLoading, validationMessages } = storeToRefs(apiStore);

const urls = ref<StoredUrl[]>(rawUrls.value);

type ApiSelectionItem = StoredUrl | HaskoningComboboxOption;

function toApiKey(item: ApiSelectionItem): string {
  const pathOrUrl =
    typeof item === "object" && item !== null && "path" in item && typeof item.path === "string"
      ? item.path
      : String(item);

  return constructUrl(baseUrl.value, pathOrUrl);
}

function getSelectedChipStatus(item: HaskoningComboboxOption): HaskoningComboboxSelectedChipStatus {
  const entry = apiEntries.value.get(toApiKey(item));

  if (entry?.state === "loading") return "loading";
  if (entry?.state === "loaded") return "success";
  if (entry?.state === "error") return "error";

  return "default";
}

function hasSameSelectedApis(left: StoredUrl[], right: StoredUrl[]): boolean {
  if (left.length !== right.length) return false;

  return left.every((item, index) => toApiKey(item) === toApiKey(right[index]));
}

const hasFieldErrors = computed(() => validationMessages.value.length > 0);
const supportingText = computed(() => (urls.value.length > 0 ? "" : "No endpoints selected"));

async function commitSelectedApis(): Promise<void> {
  if (hasSameSelectedApis(urls.value, rawUrls.value)) {
    return;
  }

  await apiStore.updateSelectedApis(urls.value);
}

watch(rawUrls, (newUrls) => {
  urls.value = [...newUrls];
});
</script>

<template>
  <HaskoningCombobox
    v-model="urls"
    :items="inventory"
    item-title="name"
    item-value="path"
    label="Select API('s)"
    color="primary"
    :multiple="true"
    :clearable="true"
    :loading="isInventoryLoading"
    :error="hasFieldErrors"
    :error-messages="validationMessages"
    :supporting-text="supportingText"
    :selected-chip-status="getSelectedChipStatus"
    variant="filled"
    @blur="commitSelectedApis"
    @clear="commitSelectedApis"
    @selected-chip-remove="commitSelectedApis"
  />
</template>

<style scoped></style>
